import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	getHallmapResources,
	getResources,
	getResourcesFree,
	getResourcesState,
} from './hallmapThunk';
import { mapResourcesHallmap } from '../lib/helpers/resources/mappingHallmapResources';
import {
	HallmapResources,
	HallmapState,
	Resource,
	ResourcesFree,
	ResourcesState,
} from './types';
import { Table } from 'shared/lib/core';
import { CustomError } from 'shared/api';

const initialState: HallmapState = {
	resources: [],
	resourceTypes: [],
	layoutItems: [],
	hallmapTables: [],
	resourcesState: [],
	selectedTables: [],
	tableUsedBefore: false,
	conflictedTables: [],
	isConflict: false,
	resourcesFree: [],
	loading: false,
	error: null,
	firstClicked: false,
};

const hallmapSlice = createSlice({
	name: 'resources',
	initialState,
	reducers: {
		selectTable: (
			state,
			action: PayloadAction<{ table: Table; earlySelected: boolean, manual?: boolean }>,
		) => {
			const isFirstClick = !state.firstClicked && action.payload.manual;
			const data = action.payload;
			const table: Table = data.table;

			state.firstClicked = isFirstClick ? true : state.firstClicked;
			//TODO поправить any

			if (isFirstClick) {
				state.selectedTables = [];
				state.selectedTables.push(table as any);
			} else {
				state.selectedTables.push(table as any);
				state.selectedTables = state.selectedTables.sort(
					(a, b) => a.id - b.id,
				);
	
	
				if (!data.earlySelected) {
					state.tableUsedBefore = true;
				}
			}
		},
		deselectTable: (state, action: PayloadAction<{ table: Table }>) => {
			state.selectedTables = state.selectedTables.filter(
				table =>
					table.options.name !== action.payload.table.options.name,
			);
			state.conflictedTables = state.conflictedTables.filter(
				table => table !== action.payload.table.options.name,
			);

			if (!state.conflictedTables.length) {
				state.isConflict = false;
			}

			state.tableUsedBefore = true;
		},
		setConflitedTable: (state, action: PayloadAction<{ table: Table }>) => {
			if (
				!state.conflictedTables.includes(
					action.payload.table.options.name,
				)
			) {
				state.conflictedTables.push(action.payload.table.options.name);
				state.isConflict = true;
			}
		},
		setConflitResolvedTable: (
			state,
			action: PayloadAction<{ table: Table }>,
		) => {
			state.conflictedTables = state.conflictedTables.filter(
				table => table !== action.payload.table.options.name,
			);
			if (!state.conflictedTables.length) {
				state.isConflict = false;
			}
		},
		resetTables: state => {
			state.conflictedTables = [];
			state.selectedTables = [];
			state.isConflict = false;
			state.firstClicked = false;
		},
	},
	extraReducers: builder => {
		builder
			.addCase(getHallmapResources.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				getHallmapResources.fulfilled,
				(state, action: PayloadAction<HallmapResources>) => {
					state.resources = action.payload.resources;
					state.resourceTypes = action.payload.resourceTypes;
					state.layoutItems = action.payload.layoutItems;

					const hallmapTables = mapResourcesHallmap({
						resources: state.resources,
						resourceTypes: state.resourceTypes,
						layoutItems: state.layoutItems,
					});

					if (hallmapTables) {
						state.hallmapTables = hallmapTables;
					}

					state.loading = false;
				},
			)
			.addCase(getHallmapResources.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as CustomError;
			})

			.addCase(getResourcesState.pending, state => {
				state.error = null;
			})
			.addCase(
				getResourcesState.fulfilled,
				(state, action: PayloadAction<ResourcesState[]>) => {
					state.resourcesState = action.payload;
				},
			)
			.addCase(getResourcesState.rejected, (state, action) => {
				state.error = action.payload as CustomError;
			})

			.addCase(getResourcesFree.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				getResourcesFree.fulfilled,
				(state, action: PayloadAction<ResourcesFree[]>) => {
					state.resourcesFree = action.payload;

					state.loading = false;
				},
			)
			.addCase(getResourcesFree.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as CustomError;
			})

			.addCase(getResources.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				getResources.fulfilled,
				(state, action: PayloadAction<Resource[]>) => {
					state.resources = action.payload;
				},
			)
			.addCase(getResources.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as CustomError;
			});
	},
});

export const { actions, reducer } = hallmapSlice;
export const hallmapReducer = reducer;
export const {
	selectTable,
	deselectTable,
	setConflitedTable,
	setConflitResolvedTable,
	resetTables,
} = actions;
